import { useCallback, useEffect, useState } from "react";
import { DollarSign, TrendingUp, Users, AlertTriangle, Wallet, Percent, Loader2, Plus, Trash2, Tag } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function Stat({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div className="rounded-2xl border border-white/8 bg-[#121214] p-5" data-testid={testid}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${accent ? "fv-gold-gradient" : "bg-[#D4AF37]/10 border border-[#D4AF37]/20"}`}>
        <Icon className={`w-4 h-4 ${accent ? "text-[#0A0A0B]" : "text-[#D4AF37]"}`} />
      </div>
      <div className="font-heading text-2xl text-white tabular-nums">{value}</div>
      <div className="text-[#71717A] text-sm mt-0.5">{label}</div>
      {sub && <div className="text-[11px] text-[#52525B] mt-0.5">{sub}</div>}
    </div>
  );
}

export default function AdminFinance() {
  const [d, setD] = useState(null);
  const [pricing, setPricing] = useState([]);
  const [promos, setPromos] = useState([]);
  const [denied, setDenied] = useState(false);
  const [promo, setPromo] = useState({ code: "", percent_off: "", description: "" });
  const [priceEdit, setPriceEdit] = useState({});

  const load = useCallback(async () => {
    try {
      const [f, p, pr] = await Promise.all([
        api.get("/admin/finance"),
        api.get("/admin/pricing").catch(() => ({ data: { plans: [] } })),
        api.get("/admin/promotions").catch(() => ({ data: { promotions: [] } })),
      ]);
      setD(f.data); setPricing(p.data.plans); setPromos(pr.data.promotions);
    } catch (e) { if (e.response?.status === 403) setDenied(true); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const savePrice = async (tier) => {
    const price = parseFloat(priceEdit[tier]);
    if (isNaN(price)) return;
    try { await api.put("/admin/pricing", { tier, price }); toast.success("Price updated"); setPriceEdit({}); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const createPromo = async () => {
    try {
      await api.post("/admin/promotions", { code: promo.code, percent_off: parseInt(promo.percent_off, 10), description: promo.description });
      toast.success("Promotion created"); setPromo({ code: "", percent_off: "", description: "" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delPromo = async (code) => { try { await api.delete(`/admin/promotions/${code}`); toast.success("Deleted"); load(); } catch (e) { toast.error("Failed"); } };

  if (denied) return <p className="text-sm text-[#71717A]" data-testid="admin-finance-denied">You don't have the "finance.view" permission.</p>;
  if (!d) return <div className="flex justify-center py-20 text-[#71717A]"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const maxRev = Math.max(...d.monthly_revenue.map((m) => m.revenue), 1);

  return (
    <div data-testid="admin-finance" className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={DollarSign} label="MRR" value={money(d.mrr)} accent testid="fin-mrr" />
        <Stat icon={TrendingUp} label="ARR" value={money(d.arr)} testid="fin-arr" />
        <Stat icon={Wallet} label="Total revenue" value={money(d.total_revenue)} testid="fin-revenue" />
        <Stat icon={Percent} label="Revenue growth" value={`${d.revenue_growth}%`} sub="vs last month" testid="fin-growth" />
        <Stat icon={DollarSign} label="ARPU" value={money(d.arpu)} sub="per paying user" testid="fin-arpu" />
        <Stat icon={TrendingUp} label="Est. LTV" value={money(d.ltv)} testid="fin-ltv" />
        <Stat icon={Users} label="Active trials" value={d.active_trials} testid="fin-trials" />
        <Stat icon={AlertTriangle} label="Failed / refunds" value={`${d.failed_payments} / ${d.refunds}`} testid="fin-failed" />
      </div>

      {/* Monthly revenue chart */}
      <div>
        <h3 className="text-white font-medium mb-3">Monthly revenue (last 6 months)</h3>
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-6 flex items-end gap-3 h-52" data-testid="fin-revenue-chart">
          {d.monthly_revenue.map((m) => (
            <div key={m.month} className="flex-1 flex flex-col items-center gap-2">
              <div className="w-full bg-gradient-to-t from-[#D4AF37]/40 to-[#D4AF37] rounded-t-md transition-all"
                style={{ height: `${Math.max((m.revenue / maxRev) * 140, 4)}px` }} title={money(m.revenue)} />
              <span className="text-[10px] text-[#71717A]">{m.month.slice(5)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* MRR by tier */}
      <div>
        <h3 className="text-white font-medium mb-3">MRR by plan</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          {d.mrr_by_tier.map((t) => (
            <div key={t.tier} className="rounded-2xl border border-white/8 bg-[#121214] p-5" data-testid={`fin-tier-${t.tier}`}>
              <div className="text-sm text-[#D4AF37]">{t.name}</div>
              <div className="font-heading text-2xl text-white mt-1">{money(t.mrr)}</div>
              <div className="text-xs text-[#71717A]">{t.count} active subscribers</div>
            </div>
          ))}
        </div>
      </div>

      {/* Pricing management */}
      <div>
        <h3 className="text-white font-medium mb-3">Pricing</h3>
        <div className="rounded-2xl border border-white/8 bg-[#121214] divide-y divide-white/5" data-testid="fin-pricing">
          {pricing.map((p) => (
            <div key={p.tier} className="flex items-center justify-between px-5 py-3 gap-4">
              <div><span className="text-white capitalize">{p.name}</span>
                {p.overridden && <span className="ml-2 text-[10px] text-amber-400">overridden (default {money(p.default_price)})</span>}</div>
              <div className="flex items-center gap-2">
                <span className="text-[#71717A] text-sm">$</span>
                <Input type="number" defaultValue={p.price} onChange={(e) => setPriceEdit({ ...priceEdit, [p.tier]: e.target.value })}
                  data-testid={`fin-price-${p.tier}`} className="bg-[#0d0d0f] border-white/10 text-white w-24 h-9" />
                <Button onClick={() => savePrice(p.tier)} data-testid={`fin-save-price-${p.tier}`} disabled={priceEdit[p.tier] === undefined}
                  className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full text-xs h-9">Save</Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Promotions */}
      <div>
        <h3 className="text-white font-medium mb-3">Promotions</h3>
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-5 mb-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input placeholder="CODE" value={promo.code} onChange={(e) => setPromo({ ...promo, code: e.target.value })} data-testid="promo-code" className="bg-[#0d0d0f] border-white/10 text-white sm:w-32" />
            <Input type="number" placeholder="% off" value={promo.percent_off} onChange={(e) => setPromo({ ...promo, percent_off: e.target.value })} data-testid="promo-percent" className="bg-[#0d0d0f] border-white/10 text-white sm:w-24" />
            <Input placeholder="Description" value={promo.description} onChange={(e) => setPromo({ ...promo, description: e.target.value })} data-testid="promo-desc" className="bg-[#0d0d0f] border-white/10 text-white flex-1" />
            <Button onClick={createPromo} data-testid="promo-create" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full"><Plus className="w-4 h-4 mr-1" /> Add</Button>
          </div>
        </div>
        <div className="space-y-2" data-testid="promo-list">
          {promos.length ? promos.map((p) => (
            <div key={p.code} className="flex items-center justify-between rounded-xl border border-white/8 bg-[#121214] px-5 py-3">
              <div className="flex items-center gap-3">
                <Tag className="w-4 h-4 text-[#D4AF37]" />
                <span className="text-white font-mono">{p.code}</span>
                <span className="text-[#D4AF37] text-sm">{p.percent_off}% off</span>
                <span className="text-[#71717A] text-xs">{p.description}</span>
              </div>
              <button onClick={() => delPromo(p.code)} data-testid={`promo-del-${p.code}`} className="text-[#71717A] hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          )) : <p className="text-sm text-[#71717A]">No promotions yet.</p>}
        </div>
      </div>
    </div>
  );
}
